import React, { useState } from 'react';
import { Button } from '@wds/button';
import { Typography } from '@wds/typography';
import { ProgressCircle } from '@wds/progress-circle';
import { getCookie } from 'sma.common/utils';

// ---------------------------------------------------------------------------
// Shared modal shell
// ---------------------------------------------------------------------------

export const ModalOverlay = ({ onClose, children }) => (
    <div
        style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 2000,
        }}
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
        <div style={{
            background: '#fff', borderRadius: 8,
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            width: '100%', maxWidth: 520, margin: 16, position: 'relative',
        }}>
            {children}
        </div>
    </div>
);

export const ModalHeader = ({ title, onClose }) => (
    <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '20px 24px 16px', borderBottom: '1px solid #e4e8eb',
    }}>
        <Typography variant='body' weight='bold' style={{ fontSize: 18, color: '#1b2a32', margin: 0 }}>
            {title}
        </Typography>
        <button
            type='button'
            onClick={onClose}
            aria-label={gettext('Close')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#637381', lineHeight: 1, padding: 4 }}
        >
            &times;
        </button>
    </div>
);

// ---------------------------------------------------------------------------
// Consent / authorization modal (shown when no site connections exist)
// ---------------------------------------------------------------------------

export const ConsentModal = ({ onProceed, onClose }) => {
    const [authorized, setAuthorized] = useState(false);

    return (
        <ModalOverlay onClose={onClose}>
            <ModalHeader title={gettext('Connect Docusign')} onClose={onClose} />
            <div style={{ padding: '20px 24px' }}>
                <Typography variant='body' style={{ fontSize: 14, color: '#1b2a32', lineHeight: '1.6', marginBottom: 16 }}>
                    {gettext(
                        'By connecting Docusign, you authorize SurveyMonkey Apply to send form data, '
                        + 'generated documents, signer information, and signature status information to '
                        + 'Docusign to process signature requests. Docusign may process this information '
                        + 'in regions outside your location.',
                    )}
                    <br /><br />
                    {gettext(
                        'Your organization is responsible for its Docusign account, including any terms, '
                        + 'subscriptions, permissions, envelope usage, fees, and applicant notices or consents.',
                    )}
                </Typography>

                <label style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                    cursor: 'pointer', marginBottom: 24,
                }}>
                    <input
                        type='checkbox'
                        checked={authorized}
                        onChange={(e) => setAuthorized(e.target.checked)}
                        style={{ marginTop: 2, flexShrink: 0, width: 18, height: 18, accentColor: '#1D9A5C', cursor: 'pointer' }}
                    />
                    <Typography variant='body' style={{ fontSize: 14, color: '#1b2a32' }}>
                        {gettext("I am authorized to connect my organization's Docusign account.")}
                    </Typography>
                </label>

                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        color='success'
                        disabled={!authorized}
                        onClick={onProceed}
                        style={{ minWidth: 120 }}
                    >
                        {gettext('Proceed')}
                    </Button>
                </div>
            </div>
        </ModalOverlay>
    );
};

// ---------------------------------------------------------------------------
// Account picker modal (shown when site already has connections)
// ---------------------------------------------------------------------------

const RadioCircle = ({ selected }) => (
    <svg width='18' height='18' viewBox='0 0 18 18' aria-hidden='true' style={{ flexShrink: 0, marginTop: 1 }}>
        <circle cx='9' cy='9' r='8' fill='none' stroke={selected ? '#1D9A5C' : '#C4CDD5'} strokeWidth='1.5' />
        {selected && <circle cx='9' cy='9' r='4.5' fill='#1D9A5C' />}
    </svg>
);

export const AccountPickerModal = ({
    connections, onContinueWithExisting, onSignWithNew, onClose, assigning,
}) => {
    const [useNew, setUseNew] = useState(false);
    const [selectedId, setSelectedId] = useState(connections[0]?.id ?? null);

    const handleContinue = () => {
        if (useNew) {
            onSignWithNew();
        } else {
            onContinueWithExisting(selectedId);
        }
    };

    return (
        <ModalOverlay onClose={onClose}>
            <ModalHeader title={gettext('Select Account')} onClose={onClose} />
            <div style={{ padding: '20px 24px' }}>
                <Typography variant='body' style={{ fontSize: 14, color: '#637381', marginBottom: 20 }}>
                    {gettext("Choose how you'd like to connect your Docusign account to get started.")}
                </Typography>

                <label style={{
                    display: 'flex', alignItems: 'flex-start', gap: 12,
                    padding: '14px 16px', cursor: 'pointer',
                    border: '1px solid', borderRadius: 6, marginBottom: 10,
                    borderColor: !useNew ? '#1D9A5C' : '#e4e8eb',
                    background: !useNew ? '#f0faf5' : '#fff',
                }}>
                    <input type='radio' name='ds-picker-promo' checked={!useNew} onChange={() => setUseNew(false)} style={{ display: 'none' }} />
                    <RadioCircle selected={!useNew} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <Typography variant='body' weight='bold' style={{ fontSize: 14, color: '#1b2a32' }}>
                            {gettext('Continue with your existing account(s)')}
                        </Typography>
                        <Typography variant='body' style={{ fontSize: 13, color: '#637381', marginBottom: 8 }}>
                            {gettext('Authorize the integration using your current Docusign session.')}
                        </Typography>
                        {!useNew && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Typography variant='body' style={{ fontSize: 13, color: '#637381', flexShrink: 0 }}>
                                    {gettext('Signed in with')}
                                </Typography>
                                <select
                                    value={selectedId ?? ''}
                                    onChange={(e) => setSelectedId(Number(e.target.value))}
                                    style={{
                                        flex: 1, padding: '6px 10px', border: '1px solid #C4CDD5',
                                        borderRadius: 4, fontSize: 13, color: '#1b2a32', background: '#fff',
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {connections.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.display_name || c.account_email}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>
                </label>

                <label style={{
                    display: 'flex', alignItems: 'flex-start', gap: 12,
                    padding: '14px 16px', cursor: 'pointer',
                    border: '1px solid', borderRadius: 6, marginBottom: 24,
                    borderColor: useNew ? '#1D9A5C' : '#e4e8eb',
                    background: useNew ? '#f0faf5' : '#fff',
                }}>
                    <input type='radio' name='ds-picker-promo' checked={useNew} onChange={() => setUseNew(true)} style={{ display: 'none' }} />
                    <RadioCircle selected={useNew} />
                    <div>
                        <Typography variant='body' weight='bold' style={{ fontSize: 14, color: '#1b2a32' }}>
                            {gettext('Sign in with a new account')}
                        </Typography>
                        <Typography variant='body' style={{ fontSize: 13, color: '#637381' }}>
                            {gettext('Use another Docusign account to authorize the integration.')}
                        </Typography>
                    </div>
                </label>

                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        color='success'
                        onClick={handleContinue}
                        disabled={assigning}
                        style={{ minWidth: 120 }}
                    >
                        {assigning
                            ? <><ProgressCircle continuous size='sm' color='light' />&nbsp;{gettext('Saving...')}</>
                            : gettext('Continue')}
                    </Button>
                </div>
            </div>
        </ModalOverlay>
    );
};

// ---------------------------------------------------------------------------
// Fallback preference modal (shown after account is assigned to a program)
// ---------------------------------------------------------------------------

export const FallbackPreferenceModal = ({ fallbackUrl, onDone }) => {
    const [choice, setChoice] = useState(true); // true = manual fallback (default)
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        setSaving(true);
        try {
            await fetch(fallbackUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': getCookie('csrftoken'),
                },
                body: JSON.stringify({ fallback_to_manual_signature: choice }),
            });
        } catch (_e) { /* best-effort */ }
        setSaving(false);
        onDone();
    };

    const optionStyle = (selected) => ({
        display: 'flex', alignItems: 'flex-start', gap: 12,
        padding: '14px 16px', cursor: 'pointer', margin: 0,
        background: selected ? '#f2f8ff' : '#fff',
    });

    return (
        <ModalOverlay onClose={onDone}>
            <ModalHeader title={gettext('Docusign connected successfully')} onClose={onDone} />
            <div style={{ padding: '20px 24px' }}>
                <Typography variant='body' style={{ fontSize: 14, color: '#637381', marginBottom: 16 }}>
                    {gettext("One quick preference before you're done. What should happen if your Docusign account encounters an issue.")}
                </Typography>

                <div style={{ border: '1px solid #e4e8eb', borderRadius: 6, overflow: 'hidden', marginBottom: 20 }}>
                    <label style={{ ...optionStyle(choice === true), borderBottom: '1px solid #e4e8eb' }}>
                        <input
                            type='radio'
                            name='ds-fallback-pref'
                            style={{ marginTop: 3, flexShrink: 0, accentColor: '#0070c9' }}
                            checked={choice === true}
                            onChange={() => setChoice(true)}
                        />
                        <div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#1b2a32' }}>{gettext('Switch to manual signing automatically')}</div>
                            <div style={{ fontSize: 13, color: '#637381', marginTop: 2 }}>
                                {gettext("Documents keep moving. You'll be notified.")}
                            </div>
                        </div>
                    </label>

                    <label style={optionStyle(choice === false)}>
                        <input
                            type='radio'
                            name='ds-fallback-pref'
                            style={{ marginTop: 3, flexShrink: 0, accentColor: '#0070c9' }}
                            checked={choice === false}
                            onChange={() => setChoice(false)}
                        />
                        <div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#1b2a32' }}>{gettext('Stop and alert me')}</div>
                            <div style={{ fontSize: 13, color: '#637381', marginTop: 2 }}>
                                {gettext('No documents are sent until you resolve it.')}
                            </div>
                        </div>
                    </label>
                </div>

                <Typography variant='body' style={{ fontSize: 12, color: '#637381', marginBottom: 20 }}>
                    {gettext('You can change this anytime in Docusign integration settings.')}
                </Typography>

                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button color='success' onClick={handleSave} disabled={saving} style={{ minWidth: 140, fontSize: 15, padding: '10px 24px' }}>
                        {saving ? gettext('Saving…') : gettext('Finish setup')}
                    </Button>
                </div>
            </div>
        </ModalOverlay>
    );
};

// ---------------------------------------------------------------------------
// Upgrade modal (shown when plan doesn't include Docusign)
// ---------------------------------------------------------------------------

export const UpgradeModal = ({ onClose }) => (
	<ModalOverlay onClose={onClose}>
		<ModalHeader title={gettext('Upgrade Plan')} onClose={onClose} />
		<div style={{ padding: '20px 24px' }}>
			<Typography variant='body' style={{ fontSize: 14, color: '#637381' }}>
				{gettext('To add Docusign to your plan, contact your account representative.')}
			</Typography>
		</div>
	</ModalOverlay>
);
